/* global chrome */
import React, {
  useEffect,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Container,
  ContainerField,
  ContainerStep,
  ContainerTitle,
  ContainerButton,
  DescriptionStep,
  HelpLink,
  HelpMessage,
  Section,
  StepText,
  TutorialLink,
  Typography,
  ErrorText,
  TimerDescription,
} from './styles';
import { withTranslation } from '../../../../core/i18n';
import TimerComponent from '../../../../core/components/timer-component';
import {
  LINKS_HREF,
  RECORDER_STATUS,
  MIN_BATTERY_LEVEL,
  STEPS,
  EXTENSION_ID,
} from './constants';
import SystemCheck from './checks/system';
import MicrophoneCheck from './checks/microphone';
import ScreenShareInstructions from './checks/screen-share-instructions';
import ScreenShareCheck from './checks/screen-share';
import DoNotClose from './checks/do-not-close-screen';

const ChromeExtension = (props) => {
  const {
    uri,
    expiresAt,
    time,
    phrases,
    hasExtensionInstalled,
    history,
    sessionId,
    onSetExpired,
    hasMinVersion,
  } = props;
  const { translate } = phrases;

  const [errorMessage, setErrorMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(STEPS.LAUNCH_STUDY);
  const [microphoneId, setMicrophoneId] = useState('');
  const [zoomClient, setZoomClient] = useState(null);
  const [status, setStatus] = useState(RECORDER_STATUS.IDLE);
  const [isSharing, setIsSharing] = useState(false);

  const setAbortStudy = () => {
    setStatus(RECORDER_STATUS.ABORTED);
  };

  // ----- ZOOM STREAMING -----
  const callbackExtension = useCallback((messageType) => {
    const message = {
      type: messageType,
    };
    chrome.runtime.sendMessage(EXTENSION_ID, message);
  }, []);

  const handleCurrentUserUpdate = useCallback(async (payload) => {
    if (payload && payload[0].sharerOn === true) {
      setIsSharing(true);
    }
    if (payload && payload[0].sharerOn === false && status !== RECORDER_STATUS.COMPLETED) {
      setAbortStudy();
      setIsSharing(false);
    }
  }, [status]);

  const handleInfo = (e) => {
    const { detail } = e;
    const {
      microphoneId: selectedMic,
    } = detail;
    setMicrophoneId(selectedMic);
  };

  // ask to chrome extension to dispatch event with extension ID
  const getExtensionId = () => {
    const data = {
      type: 'GET_EXTENSION_ID',
    };
    window.postMessage(data, '*');
  };

  useEffect(() => {
    getExtensionId();
    document.addEventListener('zoomInfoEvent', handleInfo);

    return () => {
      document.removeEventListener('zoomInfoEvent', handleInfo);
    };
  }, []);

  useEffect(() => {
    if (zoomClient) {
      zoomClient.on('user-updated', handleCurrentUserUpdate);
    }
    return () => {
      if (zoomClient) {
        zoomClient.off('user-updated', handleCurrentUserUpdate);
      }
    };
  }, [zoomClient, handleCurrentUserUpdate]);

  const handleBatteryCheck = async () => {
    const battery = await navigator.getBattery();
    if (battery.charging || battery.level > MIN_BATTERY_LEVEL) {
      setErrorMessage('');
      return false;
    }
    setErrorMessage(phrases.battery_alert_desktop);
    return true;
  };

  const handleExpiredCheck = () => {
    if ((Date.now() - new Date(expiresAt)) > 0) {
      setErrorMessage('This study has expired.');
      return true;
    }
    setErrorMessage('');
    return false;
  };

  const handleLaunchStudy = async () => {
    const hasBatteryError = await handleBatteryCheck();
    if (hasBatteryError) return;
    const isExpired = handleExpiredCheck();
    if (isExpired) return;
    setCurrentStep(STEPS.SYSTEM_CHECK);
  };

  switch (currentStep) {
    case STEPS.SYSTEM_CHECK:
      return (
        <SystemCheck
          setCurrentStep={setCurrentStep}
          history={history}
          STEPS={STEPS}
        />
      );

    case STEPS.MICROPHONE_CHECK:
      return (
        <MicrophoneCheck
          setCurrentStep={setCurrentStep}
          history={history}
          STEPS={STEPS}
        />
      );

    case STEPS.SCREEN_SHARE_INSTRUCTIONS:
      return (
        <ScreenShareInstructions
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          history={history}
        />
      );

    case STEPS.SCREEN_SHARE:
      return (
        <ScreenShareCheck
          sessionId={sessionId}
          setCurrentStep={setCurrentStep}
          STEPS={STEPS}
          history={history}
          uri={uri}
          setZoomClient={setZoomClient}
          isSharing={isSharing}
          status={status}
        />
      );

    case STEPS.DO_NOT_CLOSE:
      return (
        <DoNotClose
          microphoneId={microphoneId}
          sessionId={sessionId}
          callbackExtension={callbackExtension}
          zoomClient={zoomClient}
          setStatus={setStatus}
          status={status}
          setAbortStudy={setAbortStudy}
        />
      );

    default:
      return (
        <Container>
          <ContainerTitle>
            <Typography>{phrases.launch_test_extension_title}</Typography>
          </ContainerTitle>

          {time && (
            <Section>
              <TimerComponent time={time} onSetExpired={onSetExpired} />
              <ContainerStep>
                <TimerDescription>
                  We will reserve your spot for 10 minutes.
                  After 10 minutes, we will release your spot so that other participants can take this test.
                  Follow the steps below to start this test before the timer runs out.
                  If the timer runs out and there are still open spots, you can try again.
                </TimerDescription>
              </ContainerStep>
            </Section>
          )}

          <Section>
            <ContainerStep>
              {translate && (
                <>
                  <StepText>
                    {translate('launch_test_step', { STEP: 1 })}
                  </StepText>
                  <DescriptionStep>
                    {translate('download_computer_study_instruction_1_step', { RECORDER_TYPE: phrases.recorder_type_chrome_extension })}
                    {'. '}
                    {phrases.download_extension_step_1}
                  </DescriptionStep>
                </>
              )}
            </ContainerStep>

            {translate && (
              <ContainerButton>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={LINKS_HREF.download_extension}
                >
                  <Button>
                    {translate('install_recorder_button', { RECORDER_TYPE: phrases.recorder_type_chrome_extension })}
                  </Button>
                </a>
              </ContainerButton>
            )}

            {translate && (
              <HelpLink
                target="_blank"
                href={LINKS_HREF.first}
                aria-label="Read an article on step 1"
              >
                {translate('launch_test_help_message', { STEP: 1 })}
              </HelpLink>
            )}
          </Section>

          <Section>
            <ContainerStep>
              {translate && (
                <StepText>
                  {translate('launch_test_step', { STEP: 2 })}
                </StepText>
              )}

              <DescriptionStep>
                {phrases.download_extension_step_2}
              </DescriptionStep>
            </ContainerStep>
            <ContainerField direction="column">
              <Button
                onClick={handleLaunchStudy}
                disabled={!hasExtensionInstalled || !hasMinVersion}
              >
                {phrases.tester_page_launch_test_button}
              </Button>
              {!hasMinVersion && (
                <ErrorText>
                  {phrases.body_old_version_1}
                </ErrorText>
              )}
              {hasMinVersion && errorMessage && (
                <ErrorText>{errorMessage}</ErrorText>
              )}
            </ContainerField>
            {translate && (
              <HelpLink
                target="_blank"
                href={LINKS_HREF.second}
                aria-label="Read an article on step 2"
              >
                {translate('launch_test_help_message', { STEP: 2 })}
              </HelpLink>
            )}
          </Section>
          <HelpMessage>
            {phrases.getting_started_video_1}
            <TutorialLink href={LINKS_HREF.download_instructions} target="_blank">
              {` ${phrases.getting_started_video_2} `}
            </TutorialLink>
            {phrases.getting_started_video_3}
          </HelpMessage>

          <HelpMessage>
            {phrases.nda_terms_1}
            <TutorialLink href={LINKS_HREF.nda} target="_blank">
              {` ${phrases.nda_terms_2} `}
            </TutorialLink>
          </HelpMessage>
        </Container>
      );
  }
};

ChromeExtension.defaultProps = {
  uri: '',
  time: undefined,
};

ChromeExtension.propTypes = {
  uri: PropTypes.string,
  hasExtensionInstalled: PropTypes.bool.isRequired,
  time: PropTypes.string,
  expiresAt: PropTypes.string.isRequired,
  onSetExpired: PropTypes.func.isRequired,
  hasMinVersion: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }).isRequired,
  sessionId: PropTypes.string.isRequired,
  phrases: PropTypes.shape({
    body_old_version_1: PropTypes.string,
    recorder_type_chrome_extension: PropTypes.string,
    download_extension_step_1: PropTypes.string,
    download_extension_step_2: PropTypes.string,
    nda_terms_1: PropTypes.string,
    nda_terms_2: PropTypes.string,
    battery_alert_desktop: PropTypes.string,
    copied_to_clipboard: PropTypes.string,
    copy_button: PropTypes.string,
    download_computer_study_instruction_1: PropTypes.string,
    download_computer_study_instruction_3_step: PropTypes.string,
    getting_started_video_1: PropTypes.string,
    getting_started_video_2: PropTypes.string,
    getting_started_video_3: PropTypes.string,
    launch_test_help_message: PropTypes.string,
    launch_test_step: PropTypes.string,
    launch_test_extension_title: PropTypes.string,
    tester_page_launch_test_button: PropTypes.string,
    translate: PropTypes.func,
    your_code_instructions_box: PropTypes.string,
  }).isRequired,
};

export default withTranslation(ChromeExtension);
